import { useMutation } from '@tanstack/react-query';
import { Button, Card } from 'flowbite-react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import PasswordInput from '../../../components/passwordInput/passwordInput.component';
import { isPasswordValid } from '../../../components/passwordInput/passwordRules';
import PasswordStrengthIndicator from '../../../components/passwordInput/passwordStrengthIndicator.component';
import { ProductLogo } from '../../components/productLogo/productLogo.component';

interface FormValues {
    password: string;
    passwordConfirm: string;
}

const SetPasswordView = ({ onComplete }: { onComplete: () => void }) => {
    const api = useApi();

    const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
        defaultValues: { password: '', passwordConfirm: '' }
    });

    const doSetPassword = useMutation({
        mutationFn: (password: string) => api.v1.auth.provider.password.setInitialPassword(password),
        onSuccess: () => onComplete()
    });

    const onSubmit = useCallback((data: FormValues) => {
        doSetPassword.mutate(data.password);
    }, [doSetPassword]);

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-2">
            <Card className="p-4 w-full max-w-sm shadow-lg">
                <ProductLogo className='text-center' />
                <div className='w-full border-t border-t-gray-200' />
                <h4 className="text-xl font-semibold text-center">Nastavení hesla</h4>
                <p className="text-sm text-center text-gray-600">Pro přihlášení e-mailem a heslem si nastavte heslo.</p>
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                    <div>
                        <PasswordInput
                            placeholder="Heslo"
                            {...register('password', {
                                required: 'Heslo je povinné',
                                validate: v => isPasswordValid(v) || 'Heslo nesplňuje požadavky',
                            })}
                            color={errors.password ? 'failure' : ''}
                        />
                        {errors.password && <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>}
                    </div>
                    <div>
                        <PasswordInput
                            placeholder="Potvrďte heslo"
                            {...register('passwordConfirm', { required: 'Potvrzení hesla je povinné', validate: v => v === watch('password') || 'Hesla se neshodují' })}
                            color={errors.passwordConfirm ? 'failure' : ''}
                        />
                        {errors.passwordConfirm && <p className="text-sm text-red-500 mt-1">{errors.passwordConfirm.message}</p>}
                    </div>
                    {doSetPassword.error && <p className="text-sm text-red-500 text-center">Nastala chyba, zkuste to prosím znovu.</p>}
                    <PasswordStrengthIndicator password={watch('password')} />
                    <Button type="submit" className="w-full" color="primary" disabled={doSetPassword.isPending || !isPasswordValid(watch('password'))}>
                        {doSetPassword.isPending ? 'Ukládám...' : 'Nastavit heslo'}
                    </Button>
                </form>
            </Card>
        </div>
    );
};

export default SetPasswordView;
